import { processAPIResponse } from '@/utils/request';
import { fetchContactsRequest, fetchCustomerContactRequest, FetchContactsQuery } from '@/services/api/contact';
import { ContactListModel, ContactModel } from '@/typings/models/Contact';
import { extractContactList } from '@/utils/contacts';

export type ContactDrawerState = {
  visible: boolean;
  /** Absent → the drawer creates a new contact. */
  contactId?: string;
  /** Needed to read and save: contacts are served through their customer. */
  customerId?: string;
  /** The project it was opened from, kept in step with edits made here. */
  leadId?: string;
  toggledAt?: number;
};

export type ContactsState = {
  list: ContactListModel;
  query: FetchContactsQuery;
  /** Identifies the filter set `list` belongs to; pages of an older one are dropped. */
  queryKey: string;
  drawer: ContactDrawerState;
  details: Record<string, ContactModel>;
};

const initialState: ContactsState = {
  list: { items: [] },
  query: {},
  queryKey: '',
  drawer: { visible: false },
  details: {},
};

export const contactQueryKey = (query: FetchContactsQuery) => JSON.stringify(query);

const selectQueryKey = (state: DefaultRootState) => state.contacts.queryKey;

export default {
  namespace: 'contacts',

  state: initialState,

  effects: {
    // takeLatest: a newer search cancels the one still in flight.
    fetchList: [
      function* fetchList(
        { payload = {} }: { payload: { query?: FetchContactsQuery } },
        { call, put, select },
      ) {
        const query = payload.query ?? (yield select((state: DefaultRootState) => state.contacts.query));
        yield put({ type: 'saveQuery', payload: query });
        const response = yield call(fetchContactsRequest, { query });
        yield processAPIResponse(response, {
          *onSuccess(data: unknown) {
            yield put({ type: 'saveList', payload: { ...extractContactList(data), concat: false } });
          },
        });
      },
      { type: 'takeLatest' },
    ],

    /** The next page of the current list, by its `links.next`. */
    *fetchMore({ payload }: { payload: { url: string; queryKey: string } }, { call, put, select }) {
      const response = yield call(fetchContactsRequest, { url: payload.url });
      // The filters changed while this page loaded: it belongs to a list that's gone.
      if ((yield select(selectQueryKey)) !== payload.queryKey) return;
      yield processAPIResponse(response, {
        *onSuccess(data: unknown) {
          yield put({ type: 'saveList', payload: { ...extractContactList(data), concat: true } });
        },
      });
    },

    *fetchDetails({ payload }: { payload: { contactId: string; customerId?: string } }, { call, put }) {
      const { contactId, customerId } = payload;
      // There is no partner-wide contact detail: a contact is read through its customer.
      if (!customerId) return;
      const response = yield call(fetchCustomerContactRequest, { params: { customerId, contactId } });
      yield processAPIResponse(
        response,
        {
          *onSuccess(contact: ContactModel) {
            yield put({ type: 'saveDetails', payload: { ...contact, customer_id: customerId } });
          },
        },
        'data',
      );
    },
  },

  reducers: {
    saveQuery(state: ContactsState, { payload }: { payload: FetchContactsQuery }) {
      return { ...state, query: payload, queryKey: contactQueryKey(payload) };
    },

    saveList(state: ContactsState, { payload }: { payload: ContactListModel & { concat?: boolean } }) {
      const { concat, ...list } = payload;
      return {
        ...state,
        list: {
          ...list,
          items: concat ? [...state.list.items, ...list.items] : list.items,
        },
      };
    },

    saveDetails(state: ContactsState, { payload }: { payload: ContactModel }) {
      // The customer-contact response has no customer summary: keep the list row's.
      const row = state.list.items.find((item) => item.id === payload.id);
      return {
        ...state,
        details: { ...state.details, [payload.id]: { ...row, ...state.details[payload.id], ...payload } },
        list: {
          ...state.list,
          items: state.list.items.map((item) => (item.id === payload.id ? { ...item, ...payload } : item)),
        },
      };
    },

    openDrawer(
      state: ContactsState,
      { payload = {} }: { payload?: { contactId?: string; customerId?: string; leadId?: string } },
    ) {
      return {
        ...state,
        drawer: { ...payload, visible: true, toggledAt: Date.now() },
      };
    },

    closeDrawer(state: ContactsState) {
      return { ...state, drawer: { ...state.drawer, visible: false } };
    },

    reset() {
      return initialState;
    },
  },
};
